import type { NextPage } from 'next';
import React, { useRef } from 'react';
import LazyLoad from 'react-lazyload';

import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import { Currency } from '@src/@types/currency';
import type { AffSub4 } from '@src/@types/microcredits';
import { Breadcrumbs } from '@src/components/Breadcrumbs/Breadcrumbs';
import { AccordionDetails } from '@src/components/details/components/AccordionDetails';
import { StickyBlock } from '@src/components/details/components/StickyBlock';
import { isShowDetailsCard } from '@src/components/details/helpers';
import { PageHead } from '@src/components/PageHead';
import { Pixel } from '@src/components/Pixel';
import { NA_KARTU_PAGE_DEFAULT_AMOUNT, NA_KARTU_PAGE_DEFAULT_TERM } from '@src/constants/filters';
import type { IAnalyticsItem } from '@src/helpers/analyticsEvents';
import { removeRouteParams, stringifyPath } from '@src/helpers/query';
import { useVisibility } from '@src/hooks/useVisibility';
import { currentLocationSelector } from '@src/reducers/locations';
import { fetchCardAdvertisingIfNeeded, fetchCardIfNeeded, selectCardState, useCard } from '@src/reducers/product';
import { naKartuPageRoute } from '@src/shared/constants/pageRoutes';
import { Layout } from 'app/Layout';
import { NoContentPlaceholder } from 'shared/ui/NoContentPlaceholder';
import { AddReview } from 'src/components/details/components/AddReview';
import { BackLink } from 'src/components/details/components/BackLink';
import { DetailsCard } from 'src/components/details/components/DetailsCard';
import { FilterControl } from 'src/components/details/components/FilterControl';
import { OrganizationRatingBlock } from 'src/components/details/components/OrganizationRatingBlock';
import { RedirectReviewButton } from 'src/components/details/components/RedirectReviewButton';

import styles from './DetailsPage.module.scss';

type Props = {
  initialAmount?: number;
  initialTerm?: number;
};

export const DetailsPage: NextPage = ({ initialAmount, initialTerm }: Props) => {
  const [amount, setAmount] = React.useState(initialAmount || NA_KARTU_PAGE_DEFAULT_AMOUNT);
  const [term, setTerm] = React.useState(initialTerm || NA_KARTU_PAGE_DEFAULT_TERM);

  const card = useCard();
  const isMobile = useIsMobile();

  const detailsCardRef = useRef<HTMLDivElement>(null);
  const isDetailsCardVisible = useVisibility(detailsCardRef);

  if (!isShowDetailsCard(card)) {
    return (
      <div className={styles.layout}>
        <NoContentPlaceholder
          message="Вернитесь ко списку всех займов и выберите другое предложение"
          title="Данные устарели"
          buttonText="Смотреть все предложения"
          backLinkRoute={naKartuPageRoute}
        />
      </div>
    );
  }

  const { name, organization, alias, advertising, _id } = card;
  const monetization = advertising?.monetization;
  const reviewLink = `/zaimy/mfo/${organization?.alias}/otzyvy/add/`;

  const breadcrumbs = [
    { title: 'Список займов', url: naKartuPageRoute },
    {
      title: organization?.name,
      url: `/zaimy/mfo/${organization?.alias}/${alias}/?amount=${initialAmount}&term=${initialTerm}`,
    },
  ];

  const affSub4: AffSub4 = {
    organizationName: organization?.name,
    productName: name,
    organizationId: organization?._id,
    productId: _id,
  };

  const analytics: IAnalyticsItem = {
    eventAction: card.advertising.buttonEventAction,
    advertising: card.advertising,
    _id: card._id,
    name: card.name,
    organization: { name: card.organization.name, _id: card.organization._id },
  };

  const source = `search|position_${card.advertising.position}`;
  const eventLabel = `${card.organization.name}|${card.name}|${card.advertising.offerId}|${source}`;

  return (
    <Layout>
      <PageHead />

      {monetization?.pixels?.display ? (
        <Pixel source="search_product" link={monetization.pixels.display} affSub4={affSub4} />
      ) : null}

      <div className={styles.layout}>
        {!isMobile && (
          <LazyLoad>
            <BackLink eventLabel={eventLabel} text="К списку займов" link={naKartuPageRoute} />
          </LazyLoad>
        )}
        <div className={styles.container}>
          <div className={styles.mainColumn}>
            <FilterControl
              initialAmount={amount}
              initialTerm={term}
              setAmount={setAmount}
              setTerm={setTerm}
              eventLabel={eventLabel}
              rubRates={card?.rates?.[Currency.RUB]}
              offerId={card.advertising.offerId}
            />

            <div ref={detailsCardRef}>
              <DetailsCard analytics={analytics} amount={amount} term={term} affSub4={affSub4} card={card} />
            </div>

            <AccordionDetails affSub4={affSub4} card={card} analytics={analytics} />

            {isMobile && <OrganizationRatingBlock card={card} />}

            {isMobile && (
              <RedirectReviewButton
                link={reviewLink}
                text="Оставить свой отзыв о займе"
                organizationName={organization.name}
                advertising={card.advertising}
                name={card.name}
                className={styles.reviewButtonWrapper}
              />
            )}

            {breadcrumbs.length && !isMobile && (
              <Breadcrumbs className={styles.breadcrumbs} isHomeVisible={false} items={breadcrumbs} />
            )}
          </div>
          {!isMobile && (
            <div className={styles.rightColumn}>
              <div className={styles.rightBlock}>
                <AddReview
                  organizationName={card.organization.name}
                  reviewLink={reviewLink}
                  advertising={card.advertising}
                  name={card.name}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {isMobile && !isDetailsCardVisible && (
        <StickyBlock
          amount={amount}
          term={term}
          affSub4={affSub4}
          analytics={analytics}
          logo={card.organization?.logotypes?.android}
          advSub={card.advertising?.advSub}
        />
      )}
    </Layout>
  );
};

DetailsPage.getInitialProps = async (ctx: Application.ReduxNextPageContext) => {
  const {
    store: { dispatch, getState },
    query,
    res,
  } = ctx;

  const queryOrganizationAlias = query.organizationAlias as string | undefined;
  const queryProductAlias = query.productAlias as string | undefined;
  const { route } = currentLocationSelector(getState());

  const initialAmount = query.amount ? Number(query.amount) : NA_KARTU_PAGE_DEFAULT_AMOUNT;
  const initialTerm = query.term ? Number(query.term) : NA_KARTU_PAGE_DEFAULT_TERM;

  if (queryOrganizationAlias && queryProductAlias) {
    await Promise.all([
      dispatch(
        fetchCardIfNeeded({
          organizationAlias: queryOrganizationAlias,
          alias: queryProductAlias,
        }),
      ),
    ]);
  }

  const cardState = selectCardState(getState());

  if (cardState?.error?.response?.status && res) {
    const {
      error: {
        response: { status, statusText },
      },
    } = cardState;

    res.statusCode = status;

    return { error: { statusCode: status, statusMessage: statusText } };
  }

  const { source: { _id: sourceId, alias: productAlias, organization } = {} } = cardState;

  if (res && organization && (productAlias !== queryProductAlias || organization.alias !== queryOrganizationAlias)) {
    res.writeHead(302, {
      Location: stringifyPath(`/zaimy/mfo/${organization.alias}/${productAlias}/`, removeRouteParams(query)),
    });

    res.end();

    return {};
  }

  if (sourceId) {
    await dispatch(fetchCardAdvertisingIfNeeded({ id: sourceId, location: route }));
  }

  return {
    initialAmount,
    initialTerm,
  };
};
